import './styles/globals.css';
import 'video.js/dist/video-js.css';
import './styles/videojs-skin.css';

import u from 'umbrellajs';
import { RemoteClient, LocalClient, ChaosClient, type Client } from './client';
import { CommandStream, type PlaylistCommand } from './client/commands';
import {
    PlaylistClass,
    type Playlist,
    type PlaylistLogoPosition,
} from './playlist';
import {
    VideoLayerClass,
    type VideoLayer,
    type VideoLayerState,
    AudioLayerClass,
    type AudioLayer,
} from './layer';
import {
    pickNext,
    pickSwitchTarget,
    sortBySequence,
    type LayerCandidate,
} from './layer/order';
import env from './env';

const numberOfLayers = 3;

const videoLayers: VideoLayer[] = [];
let audioLayer: AudioLayer | null = null;

const main = async function () {
    let client: Client;

    const params = new URLSearchParams(document.location.search);
    const debug = params.get('debug') == 'true' || env.debug;

    try {
        if (env.local) {
            client = new LocalClient(
                env.localStreamingUrl,
                env.localStreamingToken,
            );
        } else {
            const cid = params.get('cid') || env.cid;
            const pid = params.get('pid') || env.pid;
            const user = params.get('user') || env.user;
            const pass = params.get('pass') || env.pass;

            client = new RemoteClient({
                user,
                pass,
                cid,
                pid,
            });
        }

        client = new ChaosClient(client);
    } catch (error) {
        console.error('failed to initialize client');
        return;
    }

    const videoLayersElm = u('#videoLayers').empty();

    let commands: CommandStream | null = null;
    // Set once the playlist control below exists.
    let onCommandsDisabled: (() => void) | null = null;

    const playlist = new PlaylistClass(client);
    playlist.onNewPlaylist((playlist: Playlist) => {
        audioLayer?.setSource('#audio', playlist.audio_url);

        for (let i = 0; i < numberOfLayers; i++) {
            if (!videoLayers[i]) {
                continue;
            }
            videoLayers[i].setTemplate(playlist.template_name);
        }

        commands?.setEnabled(playlist.sse_enabled === true);
        if (playlist.sse_enabled !== true) {
            onCommandsDisabled?.();
        }
    });
    await playlist.refresh();

    if (playlist.playlist.display_safearea) {
        const safearea = u('#safearea').first();
        if (safearea) {
            (safearea as HTMLElement).style.display = 'block';
        }
    }

    if (playlist.playlist.logo) {
        const positionStyles: { [key in PlaylistLogoPosition]: string } = {
            'top-right': 'top:0; right:0;',
            'top-left': 'top:0; left:0;',
            'bottom-right': 'bottom:0; right:0',
            'bottom-left': 'bottom:0; left:0',
        };

        const logo = playlist.playlist.logo;
        const positionStyle = positionStyles[logo.position];
        const margin = logo.margin || '';
        const width = logo.width || '30vw';
        const image = `${logo.image}`;

        videoLayersElm.append(
            `<div style="position:absolute; opacity:1; z-index:999; margin:${margin}; ${positionStyle}"><image src="${image}" alt="Logo" style="width:${width}"/></div>`,
        );
    }

    const makeid = function (length: number) {
        const characters =
            'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;

        let result = '#';
        for (let i = 0; i < length; i++) {
            result += characters.charAt(
                Math.floor(Math.random() * charactersLength),
            );
        }
        return result;
    };

    // Layers that main is loading new content into. A manual switch must not
    // pick them, and automatic reloads leave the manual switch target alone.
    const busy = new Set<VideoLayer>();
    // Content loaded before the latest manual switch is out of sequence and
    // only serves as a reserve (see layer/order.ts).
    let epoch = 0;
    const loadedIn = new Map<VideoLayer, number>();

    // playlist_pause suspends automatic slot transitions, not media playback.
    let paused = false;
    let pausedReport: ReturnType<typeof setInterval> | null = null;
    // playlist_start continues with the next item as soon as one is ready.
    let resumePending = false;
    // A player_switch waiting for its target to load. loaded is set once the
    // target layer holds the requested item, not its previous content.
    let pendingSwitch: {
        index: number;
        layer: VideoLayer | null;
        loaded: boolean;
    } | null = null;

    const activeLayer = () =>
        videoLayers.find((layer) => layer.isActive()) ?? null;

    const playlistLength = () => playlist.playlist.playlist.length;

    const isSwitchTarget = (layer: VideoLayer) =>
        pendingSwitch !== null && pendingSwitch.layer === layer;

    const describe = (layer: VideoLayer): LayerCandidate => ({
        index: layer.getSource()?.index ?? null,
        ready: layer.getState() === 'playing' && !busy.has(layer),
        stale: (loadedIn.get(layer) ?? epoch) < epoch,
    });

    const load = async (
        layer: VideoLayer,
        context: string,
        source: Awaited<ReturnType<typeof playlist.getNextContent>>,
        options?: { fast?: boolean },
    ) => {
        loadedIn.set(layer, epoch);
        await layer.setSource(context, source, options);
    };

    // Loads the next item of the automatic sequence into a layer.
    const loadNext = async (layer: VideoLayer, context: string) => {
        busy.add(layer);
        try {
            await load(layer, context, await playlist.getNextContent());
        } finally {
            busy.delete(layer);
        }
        // A switch target may have been waiting for a free layer.
        assignSwitchTarget(context);
    };

    // A manual switch restarts the sequence after its target. Everything
    // loaded before it becomes a reserve.
    const applyManualSwitch = (next: VideoLayer) => {
        if (!pendingSwitch?.loaded || next !== pendingSwitch.layer) {
            return;
        }

        const index = next.getSource()?.index;
        pendingSwitch = null;
        epoch++;
        loadedIn.set(next, epoch);
        if (index !== undefined) {
            playlist.continueAfter(index);
        }
    };

    const switchToNextLayer = async function (
        context: string,
        preferred: VideoLayer | null = null,
    ) {
        let current = activeLayer();
        const inactive = videoLayers.filter((layer) => !layer.isActive());

        console.log(
            context,
            'layers: inactive',
            inactive.length,
            'active',
            current?.getStatus().name,
        );

        if (inactive.length === 0) {
            console.warn(
                context,
                'no inactive layers, nothing to switch to',
                inactive.length,
            );
            return;
        }

        if (preferred === null) {
            // Check for layers in unknown state.
            for (const layer of inactive) {
                if (
                    layer.getState() != 'unknown' ||
                    busy.has(layer) ||
                    isSwitchTarget(layer)
                ) {
                    continue;
                }

                console.warn(
                    context,
                    layer.getStatus().name,
                    'unknown layer state -> setting new source',
                );

                await loadNext(layer, context);
            }

            // Loading the sources above takes a while. If another switch ran in
            // the meantime, the active and inactive layers found above are outdated.
            if (
                current === null ? activeLayer() !== null : !current.isActive()
            ) {
                console.log(
                    context,
                    'layers changed meanwhile, nothing to switch',
                );
                return;
            }
        }

        // The manual switch target only plays through its own switch.
        const candidates = inactive.filter(
            (layer) =>
                layer === preferred ||
                (!busy.has(layer) && !isSwitchTarget(layer)),
        );
        const length = playlistLength();

        const preferredReady =
            preferred !== null &&
            !preferred.isActive() &&
            preferred.getState() === 'playing';

        if (current === null) {
            // If there's no active layer, activate the next playing layer.
            const first = preferredReady
                ? preferred
                : pickNext(candidates, describe, null, length);
            if (first === null) {
                return;
            }
            applyManualSwitch(first);

            console.warn(
                context,
                'no active layer, choosing',
                first.getStatus().name,
            );

            // Activate the layer and move it to the front z-index. All other layers will be moved to the back.
            first.setActive(true, numberOfLayers * 10);
            const rest = inactive.filter((layer) => layer !== first);
            for (let i = 0; i < rest.length; i++) {
                rest[i].setActive(false, (rest.length - i) * 10);
            }

            return;
        }

        let next: VideoLayer | null = preferredReady
            ? preferred
            : pickNext(
                  candidates,
                  describe,
                  current.getSource()?.index ?? null,
                  length,
              );

        if (Math.random() > 1 - env.chaosNoNextLayer) {
            console.log(context, 'chaos: no next layer to switch to available');
            next = null;
        }

        if (next === null) {
            console.warn(context, 'no playing layer available to switch to');

            // What to do?
            // Iterate through all inactive layers and set a new source. This will cause a state change.
            for (const layer of inactive) {
                if (busy.has(layer) || isSwitchTarget(layer)) {
                    continue;
                }
                await loadNext(layer, context);
            }

            // The currently playing layer already finished by timeout and will not trigger again and will keep
            // playing if not forcefully finished. Another switch may have replaced it while loading.
            if (current.isActive()) {
                current.forceFinish();
            }

            return;
        }

        const previous = current;

        applyManualSwitch(next);

        console.log(
            context,
            `switching layers ${current.getStatus().name} -> ${
                next.getStatus().name
            }`,
        );

        // The outgoing layer reloads after its fade-out: no manual switch may take it.
        busy.add(previous);

        // Deactivate the current layer and lift it above the top layer for the transition.
        current.setActive(false, (numberOfLayers + 1) * 10, async () => {
            // Set a new source for the previously active layer.
            try {
                await load(previous, context, await playlist.getNextContent());
            } finally {
                busy.delete(previous);
            }

            // Order the inactive layers by playback, the next one on top.
            // Loading the source may outlast the slot: skip layers a later switch has activated since.
            const stillInactive = sortBySequence(
                videoLayers.filter((layer) => !layer.isActive()),
                describe,
                activeLayer()?.getSource()?.index ?? null,
                playlistLength(),
            );
            for (let i = 0; i < stillInactive.length; i++) {
                stillInactive[i].setActive(
                    false,
                    (stillInactive.length - i) * 10,
                );
            }

            assignSwitchTarget(context);
        });

        // Activate the next layer.
        if (next.hasAudio()) {
            audioLayer?.setActive(false);
        } else {
            audioLayer?.setActive(true);
        }
        next.setActive(true, numberOfLayers * 10);

        console.log(context, 'done');
        console.log('ping');
    };

    // Loads a manual switch target into a free layer. Runs again whenever a
    // layer becomes free, in case none was free when the command arrived.
    const assignSwitchTarget = async (
        context: string,
        reuse: VideoLayer | null = null,
    ) => {
        const request = pendingSwitch;
        if (request === null || request.layer !== null) {
            return;
        }

        const target =
            reuse !== null && !reuse.isActive()
                ? reuse
                : pickSwitchTarget(
                      videoLayers.filter(
                          (layer) => !layer.isActive() && !busy.has(layer),
                      ),
                      describe,
                      activeLayer()?.getSource()?.index ?? null,
                      playlistLength(),
                  );
        if (target === null) {
            return;
        }

        request.layer = target;
        console.log(
            context,
            `loading position ${request.index} into ${target.getStatus().name}`,
        );

        const content = await playlist.getContentAt(request.index);
        // A newer command or playlist_start replaced this one meanwhile.
        if (pendingSwitch !== request) {
            return;
        }
        if (content === null) {
            console.warn(
                context,
                `switch to position ${request.index} failed, staying on the current item`,
            );
            pendingSwitch = null;
            return;
        }

        request.loaded = true;
        await load(target, context, content, { fast: true });
    };

    const requestSwitch = async (index: number, context: string) => {
        // The API only sends switches after a confirmed pause, but a stale or
        // repeated command must still never move a running rotation.
        if (!paused) {
            console.warn(
                context,
                `player_switch to ${index} ignored: playlist is not paused`,
            );
            return;
        }
        if (index >= playlistLength()) {
            console.warn(
                context,
                `player_switch to ${index} ignored: no such position`,
            );
            return;
        }

        // A newer switch takes over the layer of a pending one.
        const reuse = pendingSwitch?.layer ?? null;
        pendingSwitch = { index, layer: null, loaded: false };

        // Already loaded and ready in an inactive layer: switch right away.
        const ready = videoLayers.find((layer) => {
            const source = layer.getSource();
            return (
                !layer.isActive() &&
                !busy.has(layer) &&
                layer.getState() === 'playing' &&
                source?.index === index &&
                source.playlist_revision === playlist.revision()
            );
        });
        if (ready !== undefined) {
            pendingSwitch.layer = ready;
            pendingSwitch.loaded = true;
            await switchToNextLayer(context, ready);
            return;
        }

        await assignSwitchTarget(context, reuse);
    };

    const pause = (context: string) => {
        if (paused) {
            return;
        }

        paused = true;
        resumePending = false;
        for (const layer of videoLayers) {
            layer.setAutoAdvance(false);
        }
        activeLayer()?.reportState();
        pausedReport = setInterval(() => activeLayer()?.reportState(), 30000);
        console.log(context, 'playlist paused');
    };

    const resume = async (context: string) => {
        if (!paused) {
            return;
        }

        paused = false;
        if (pausedReport !== null) {
            clearInterval(pausedReport);
            pausedReport = null;
        }
        // Start cancels a pending manual load. Its layer holds an item out of
        // sequence, which makes it a reserve until it is reloaded.
        if (pendingSwitch?.layer) {
            loadedIn.set(pendingSwitch.layer, -1);
        }
        pendingSwitch = null;
        // The active item gets a fresh deadline, which also covers the case
        // that nothing else becomes ready.
        for (const layer of videoLayers) {
            layer.setAutoAdvance(true);
        }
        console.log(context, 'playlist started');

        const current = activeLayer();
        if (current === null) {
            return;
        }

        // Continue with the item after the active one, without playing the
        // rest of its slot. A reserve from before a manual switch only plays
        // when no item of the current sequence is on its way.
        const inactive = videoLayers.filter(
            (layer) => !layer.isActive() && !busy.has(layer),
        );
        const next = pickNext(
            inactive,
            describe,
            current.getSource()?.index ?? null,
            playlistLength(),
        );
        const sequenceLoading = inactive.some((layer) => {
            const { ready, stale } = describe(layer);
            return !ready && !stale;
        });
        if (next !== null && (!describe(next).stale || !sequenceLoading)) {
            await switchToNextLayer(context, next);
            return;
        }

        resumePending = true;
        current.reportState();
    };

    // After a manual switch, layers can still hold content from before it.
    // Once the sequence has a ready next layer again, reload them in order.
    const resequence = (context: string) => {
        if (paused || pendingSwitch !== null) {
            return;
        }

        const inactive = videoLayers.filter(
            (layer) => !layer.isActive() && !busy.has(layer),
        );
        if (
            !inactive.some(
                (layer) => describe(layer).ready && !describe(layer).stale,
            )
        ) {
            return;
        }

        const stale = inactive.find((layer) => describe(layer).stale);
        if (stale !== undefined) {
            console.log(
                context,
                stale.getStatus().name,
                'holds content from before a manual switch -> loading the sequence',
            );
            loadNext(stale, context);
        }
    };

    // Commands run one after another, so a pause is applied before a switch.
    let commandQueue: Promise<void> = Promise.resolve();
    const handleCommand = (command: PlaylistCommand) => {
        commandQueue = commandQueue
            .then(async () => {
                const context = makeid(5);
                console.log(
                    context,
                    'playlist command',
                    command.action,
                    command.action === 'player_switch' ? command.index : '',
                );

                if (command.playlist_revision !== playlist.revision()) {
                    await playlist.refresh();
                }
                if (command.playlist_revision !== playlist.revision()) {
                    console.warn(
                        context,
                        `ignoring playlist command for revision ${command.playlist_revision}`,
                    );
                    return;
                }

                if (command.action === 'player_switch') {
                    await requestSwitch(command.index, context);
                } else if (command.action === 'playlist_pause') {
                    pause(context);
                } else {
                    await resume(context);
                }
            })
            .catch((error) => {
                console.error('playlist command failed', error);
            });
    };

    const onState = async function (
        id: number,
        name: string,
        src: string,
        state: VideoLayerState,
    ): Promise<void> {
        const context = makeid(5);
        const layer = videoLayers[id];

        console.log(
            context,
            name,
            `state: pid=${id}, src=${src}, state=${state}`,
        );

        if (state === 'playing') {
            // A manual switch target is ready: show it right away.
            if (pendingSwitch?.loaded && pendingSwitch.layer === layer) {
                if (!layer.isActive()) {
                    await switchToNextLayer(context, layer);
                }
                return;
            }

            if (activeLayer() !== null) {
                // playlist_start was waiting for the next item of the sequence.
                if (
                    resumePending &&
                    !paused &&
                    !layer.isActive() &&
                    !describe(layer).stale
                ) {
                    resumePending = false;
                    await switchToNextLayer(context, layer);
                    return;
                }

                resequence(context);
                return;
            }

            console.log(
                context,
                "switching layer because there's no active player",
            );

            await switchToNextLayer(context);

            return;
        }

        // Wenn der state nicht "playing" ist, dann ist er entweder "finished" oder "error", also Zeit zu wechseln

        console.log(context, name, 'layer active', layer.isActive());

        // Wenn der Layer aktiv ist, dann muss jetzt zum nächsten Layer gewechselt werden
        if (layer.isActive()) {
            console.log(context, name, 'switching to next layer');
            await switchToNextLayer(context);
            return;
        }

        // A failed manual switch target ends that switch; the current item stays.
        if (isSwitchTarget(layer)) {
            console.warn(context, name, 'manual switch target failed');
            pendingSwitch = null;
        }

        // The outgoing layer of a switch reloads after its fade-out anyway.
        if (busy.has(layer)) {
            return;
        }

        console.log(context, name, 'setting new source');

        // Ein inaktiver Layer hat einen Fehler gemeldet => neuen Content laden
        await loadNext(layer, context);
    };

    // setup layers
    const videoLayerTemplate = u('#videoLayerTemplate');

    console.log('layertemplate', videoLayerTemplate);

    for (let i = 0; i < numberOfLayers; i++) {
        let name = 'pl' + i;

        let layer = videoLayerTemplate.clone().find('.qwertz-layer');
        let overlay = layer.find('.qwertz-overlay');
        let player = layer.find('.qwertz-player');

        layer.attr('id', name);

        let zIndex = numberOfLayers * 10 - i * 10;

        let htmlplayer = player.first() as HTMLElement;
        htmlplayer.style.zIndex = zIndex + '';

        let htmloverlay = overlay.first() as HTMLElement;
        htmloverlay.style.zIndex = zIndex + 1 + '';

        videoLayers.push(
            new VideoLayerClass(
                i,
                layer.first() as HTMLElement,
                zIndex,
                playlist.playlist.template_name,
                playlist.playlist.map_detail || 'country',
                onState,
                client,
            ),
        );

        videoLayersElm.append(layer);
    }

    console.log('layers', videoLayers);

    audioLayer = new AudioLayerClass(u('#audio').first() as HTMLElement, -100);
    if (playlist.hasAudio()) {
        await audioLayer.setSource('#audio', playlist.getAudioUrl());
        audioLayer.setActive(true, () => {});
    }

    let source = await playlist.getNextContent();
    if (source === null) {
        console.error('failed to fill first layer with a source');
        return;
    }

    await load(videoLayers[0], '#first', source);

    // Without commands no playlist_start can arrive: continue normally.
    onCommandsDisabled = () => {
        if (paused) {
            resume(makeid(5));
        }
    };
    commands = new CommandStream(client, handleCommand, () => {
        playlist.refresh();
    });
    commands.setEnabled(playlist.playlist.sse_enabled === true);

    if (debug) {
        u('body').append(
            `<div id="debug" style="position:absolute; top:0; left:0; z-index:5000; background-color:black; opacity:0.6; padding:1em">DEBUG</div>`,
        );

        setInterval(() => {
            const debug = u('#debug').empty();
            for (let i = 0; i < videoLayers.length; i++) {
                const status = videoLayers[i].getStatus();
                debug.append(
                    `<div>${status.name} ${status.state} ${
                        status.active ? 'active' : 'inactive'
                    } ${status.vod ? 'vod' : 'live'} z${status.zindex} ${
                        status.source.length > 256
                            ? status.source.slice(0, 160) + '...'
                            : status.source
                    }</div>`,
                );
            }
        }, 500);
    }

    return;
};

await main();
